/**
 * C1 v1.6: the RRP reads (C2 v1.8 `GET /api/rrp/{market}` and `GET /api/logs/rrp-exceptions`), the RRP pane's URL
 * codec and every RRP sentence of the module. Types mirror C2's JSON field for field. Every figure is the API's string:
 * the deviation, the converted price, the parity risk and the "ended" state are C2's, never worked out here, and a
 * price the person types is judged by C2 when it is saved (the 409 opens the exception fields, as the floor does).
 * C1 v1.37 (c1-fee-reason, Price Run row 39): the fee reason on a committed price above its RRP (C2 v1.39 `above_rrp`,
 * `fee_reason` and `POST /api/rrp/fee-reason`): the cell's "Record fee reason" link, the ", fees" mark on the deviation
 * and the sentences of each refusal. Whether a price stands above its RRP is C2's `above_rrp`; nothing here compares.
 * C1 v1.42 (c1-rrp-follow-words): since C2 v1.43 an RRP follows the price committed on the anchor marketplace, so the
 * "set in Config" sentence names both ways an RRP is set, and the apply panel names each RRP a commit wrote (`rrps`).
 */
import type { QueryClient } from "@tanstack/react-query";

import { call, GRID_STALE_TIME, PricingError, type Market, type RrpWritten } from "./api";
import { COPY, humanDate, MARKET_LABEL, money, personName } from "./format";
import { CHANNEL_LABEL, csvText, LOGS_PAGE_SIZE, paramsFrom, placeQuery, storeName, whole, type LogChannel, type LogPageInfo, type LogSku, type PlaceFacets, type PlaceFilters, type PlaceParams } from "./logs";

/** The gate's rule as C2 reports it, so the screen states the rule the gate enforces (RRP-D2 and the anchor row are open). */
export type RrpGate = { channels: Array<"amazon" | "shopify">; anchor_row_gated: boolean; other_currency: string; case_skus: string };
export type RrpAnchor = { marketplace_id: string; marketplace_name: string | null };
/** The exception the row's committed price stands under (C3 v1.2 `rrp_exception`), with `ended` derived by C2 from the store's day. */
export type RrpRecord = {
  id: number; reason: string; valid_until: string; ended: boolean; rrp: string; rrp_currency: string;
  staged_by: string | null; approver: string; committed_at: string;
};
/** The fee reason in force on the row's committed price (C2 v1.39): recorded after the commit through the link, or the
 * reason the line carried when it was staged (then recorded by whoever staged it, at the commit). */
export type RrpFeeReason = { reason: string; recorded_by: string | null; recorded_at: string | null; source: "recorded" | "staged" };
/** One own-channel row of the overlay, keyed as the grid rows are: (xero_sku, marketplace_id), ALL for Shopify. */
export type RrpRow = {
  xero_sku: string; marketplace_id: string; marketplace_name: string | null; product: string; variant: string;
  channel: "amazon" | "shopify"; channel_location: string; units_per_case: number | null; is_anchor: boolean; gated: boolean;
  price: string | null; price_currency: string; price_committed_at: string | null;
  rrp: string | null; currency: string | null; not_set: string | null; comparable: boolean | null;
  fx_rate: string | null; fx_rate_date: string | null; fx_stale: boolean | null; compared_price: string | null;
  deviation_pct: string | null; parity_risk: boolean | null; anchor_price: string | null; anchor_drift: boolean | null;
  above_rrp: boolean | null; exception: RrpRecord | null; fee_reason: RrpFeeReason | null;
};
export type RrpOverlay = {
  market: Market; currency: string; anchor: RrpAnchor | null; anchor_not_set: string | null; today: string;
  gate: RrpGate; row_count: number; rows: RrpRow[];
};

/** One RRP exceptions log row, exactly as C2 v1.8 serves it. `deviation_pct` is null when the two currencies differ. */
export type RrpLogRow = {
  id: number; run_line_id: number; price_run_id: number; xero_sku: string; product: string; variant: string; market: Market;
  channel: string; channel_location: string; marketplace_id: string; marketplace_name: string | null;
  price: string; currency: string; rrp: string; rrp_currency: string; deviation_pct: string | null;
  anchor_market: string; anchor_market_name: string | null; reason: string; valid_until: string; ended: boolean;
  staged_by: string | null; approver: string; committed_at: string; written_at: string;
};
export type RrpStatus = "in_force" | "ended";
export type RrpLogFilters = { market: string | null; sku: string | null; status: string | null; from: string | null; to: string | null } & PlaceFilters;
export type RrpLogFacets = { skus: LogSku[]; markets: Market[]; statuses: RrpStatus[] } & PlaceFacets;
export type RrpLogPage = { rows: RrpLogRow[]; page: LogPageInfo; filters: RrpLogFilters; facets: RrpLogFacets; today: string };

export const RRP_KEY = ["pricing", "rrp"] as const;
export const getRrpOverlay = (market: Market) => call<RrpOverlay>(`/rrp/${market}`);
/** The fee reason body C2 v1.39 takes: the row's SKU, its marketplace unless it is a Shopify row (ALL), the reason. */
export type FeeReasonBody = { xero_sku: string; marketplace_id?: string; fee_reason: string };
export function feeReasonBody(row: RrpRow, reason: string): FeeReasonBody {
  return { xero_sku: row.xero_sku, ...(row.marketplace_id === "ALL" ? {} : { marketplace_id: row.marketplace_id }), fee_reason: reason.trim() };
}
/** Record why the row's committed price stands above its RRP; C2 answers the overlay row as it now reads. */
export const recordFeeReason = (body: FeeReasonBody) => call<RrpRow>("/rrp/fee-reason", { method: "POST", body: JSON.stringify(body) });
/** The overlay of one market. Never stale: an RRP changes the moment Config saves it, and the gate judges against it now. */
export function rrpQuery(market: Market) {
  return { queryKey: [...RRP_KEY, market] as const, queryFn: () => getRrpOverlay(market), retry: 1, staleTime: 0 };
}

export const STATUSES: RrpStatus[] = ["in_force", "ended"];
/** The RRP pane's page and filters: the shared Logs keys (`pp_lpage`, `f_market`, `f_sku`, the place filters and the period,
 * C1 v1.32) plus `f_status`. */
export type RrpLogParams = { page: number; market: string | null; sku: string | null; status: RrpStatus | null } & PlaceParams;
export function rrpLogParams(search: Record<string, unknown>): RrpLogParams {
  const shared = paramsFrom(search);
  const status = typeof search.f_status === "string" && (STATUSES as string[]).includes(search.f_status) ? (search.f_status as RrpStatus) : null;
  return { page: shared.page, market: shared.market, sku: shared.sku, status, product: shared.product, variant: shared.variant,
    channel: shared.channel, store: shared.store, location: shared.location, marketplace: shared.marketplace, from: shared.from, to: shared.to };
}
function rrpLogSearch(p: RrpLogParams, all = false): string {
  const q = new URLSearchParams();
  if (p.market) q.set("market", p.market);
  if (p.sku) q.set("sku", p.sku);
  if (p.status) q.set("status", p.status);
  placeQuery(q, p);
  if (all) q.set("export", "1");
  else {
    if (p.page > 0) q.set("page", String(p.page));
    q.set("page_size", String(LOGS_PAGE_SIZE));
  }
  return `?${q.toString()}`;
}
export const getRrpLog = (p: RrpLogParams) => call<RrpLogPage>(`/logs/rrp-exceptions${rrpLogSearch(p)}`);
/** Every exception the filters match, in one answer, for the export (C2 v1.35 `export=1`). */
export const exportRrpLog = (p: RrpLogParams) => call<RrpLogPage>(`/logs/rrp-exceptions${rrpLogSearch(p, true)}`).then(whole);
/** The RRP exceptions as a CSV, each record with its prices, reason and people as the pane shows them. */
export function rrpCsv(rows: RrpLogRow[]): string {
  return csvText(RRP_COPY.log.csv.header, rows.map((r) => [
    r.committed_at, r.xero_sku, r.product, r.variant, MARKET_LABEL[r.market] ?? r.market, CHANNEL_LABEL[r.channel as LogChannel] ?? r.channel,
    storeName(r.channel_location), r.marketplace_name ?? (r.marketplace_id !== "ALL" ? r.marketplace_id : ""), r.price, r.currency, r.rrp,
    r.rrp_currency, r.deviation_pct ?? "", r.anchor_market_name ?? r.anchor_market, r.reason,
    r.valid_until, r.ended ? RRP_COPY.log.statusLabel.ended : RRP_COPY.log.statusLabel.in_force, personName(r.staged_by), personName(r.approver),
    r.price_run_id,
  ]));
}
export const RRP_LOG_KEY = ["pricing", "logs", "rrp"] as const;
/** Keyed by the exact query it answers, as the Price and Costs panes are. */
export function rrpLogQuery(p: RrpLogParams) {
  return { queryKey: [...RRP_LOG_KEY, rrpLogSearch(p)] as const, queryFn: () => getRrpLog(p), retry: 1, staleTime: GRID_STALE_TIME };
}
/** After any commit attempt: a commit moves committed prices and writes exception records, and a refusal may follow an
 * RRP change in Config, so the overlay and the RRP log are read again either way. */
export function refreshAfterCommit(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: RRP_KEY });
  void client.invalidateQueries({ queryKey: RRP_LOG_KEY });
}

/** The overlay read of each market on view: the data once answered, and whether the read failed. */
export type RrpRead = { overlay: RrpOverlay | null; failed: boolean };
export type RrpReads = Partial<Record<Market, RrpRead>>;
export type RrpLookup =
  | { kind: "pending" }
  | { kind: "failed" }
  | { kind: "none"; overlay: RrpOverlay }
  | { kind: "row"; row: RrpRow; overlay: RrpOverlay };
/** The overlay row for a price's place (the SKU whose price it is, and its marketplace or ALL), or why there is none. */
export function rrpLookup(reads: RrpReads | undefined, market: Market, sku: string, place: string): RrpLookup {
  const read = reads?.[market];
  if (!read || (!read.overlay && !read.failed)) return { kind: "pending" };
  if (!read.overlay) return { kind: "failed" };
  const row = read.overlay.rows.find((r) => r.xero_sku === sku && r.marketplace_id === place);
  return row ? { kind: "row", row, overlay: read.overlay } : { kind: "none", overlay: read.overlay };
}
/** The store's day from the market's overlay (the earliest until date C2 accepts), whether or not the row is in it. */
export function storeToday(lookup: RrpLookup | undefined): string | undefined {
  return lookup && (lookup.kind === "row" || lookup.kind === "none") ? lookup.overlay.today : undefined;
}

/** The exception fields of a stage body: both trimmed, neither when both are empty, null when only one is given (C2 would
 * refuse it with 422 bad_exception, so the box says so and sends nothing). */
export function exceptionFields(reason: string, until: string): { rrp_exception_reason?: string; rrp_exception_until?: string } | null {
  const why = reason.trim();
  const when = until.trim();
  if (!why && !when) return {};
  if (!why || !when) return null;
  return { rrp_exception_reason: why, rrp_exception_until: when };
}

/** "-4.3% vs RRP", "+1.9% vs RRP", "0.0% vs RRP": C2's one-decimal string as sent, a "+" added to a positive one for
 * reading, no arithmetic. C2's "-0.0" (a price a fraction under the RRP, judged below at four decimals) keeps its sign. */
export function deviationLabel(pct: string, fees = false): string {
  return `${pct.startsWith("-") || pct === "0.0" ? pct : `+${pct}`}% vs RRP${fees ? RRP_COPY.feeReason.mark : ""}`;
}
/** ", fees" follows the deviation when a reason is recorded on a price C2 judges above its RRP (M25 `rrpCellHtml`). */
export function feesMarked(row: RrpRow): boolean {
  return row.fee_reason !== null && row.above_rrp === true;
}
/** The link is offered on a Shopify row whose committed price stands above its RRP with no reason yet (M25; an Amazon row
 * takes a record through C2 but shows no link). A recorded reason is replaced by recording another, never edited. */
export function offersFeeReason(row: RrpRow): boolean {
  return row.channel === "shopify" && row.above_rrp === true && row.fee_reason === null;
}
/** "Fee reason: {reason} (Bojan, 24 Sept 2026)"; with no recorder and no date, the reason alone (never "unknown"). */
export function feeReasonText(fee: RrpFeeReason): string {
  const facts = [fee.recorded_by ? personName(fee.recorded_by) : null, fee.recorded_at ? humanDate(fee.recorded_at) : null].filter((x): x is string => x !== null);
  return RRP_COPY.feeReason.line(fee.reason, facts.join(", "));
}
/** The sentence for a refused fee reason, in words, never C2's field names. */
export function feeReasonRefusal(error: unknown, areaName: string): string {
  if (!(error instanceof PricingError)) return COPY.unreachable;
  const reason = error.reason();
  const facts = error.facts();
  if (reason === "not_above_rrp") return RRP_COPY.feeReason.notAbove(money(facts.rrp, facts.rrp_currency));
  if (reason === "no_rrp") return RRP_COPY.feeReason.noRrp;
  if (reason === "rrp_not_comparable") return RRP_COPY.feeReason.notComparable;
  if (reason === "no_committed_price") return RRP_COPY.feeReason.noPrice;
  if (reason === "bad_fee_reason") return RRP_COPY.feeReason.bad;
  if (error.status === 401 || error.status === 403) return COPY.noEdit(areaName);
  return facts.message ?? COPY.unreachable;
}
/** The rule the gate enforces, in words, from C2's `gate` (so a ruling on RRP-D2 changes the screen with no portal change). */
export function gateRule(gate: RrpGate): string {
  const places = gate.channels.map((c) => (c === "amazon" ? (gate.anchor_row_gated ? "Amazon" : "Amazon (every marketplace but the anchor)") : "Shopify"));
  const who = places.length === 0 ? "no channel" : places.join(" or ");
  // A value this screen does not know is named as sent, never dropped, so the legend never states a narrower rule.
  const currency = gate.other_currency === "ecb_latest_rate"
    ? " A price in another currency is compared at the latest stored rate (the ECB’s; for the dirham, the ECB’s US dollar rate times the UAE peg); with no stored rate it cannot be compared and also needs an exception."
    : ` A price in another currency is judged by the rule "${gate.other_currency}".`;
  const cases = gate.case_skus === "not_judged" ? " A case SKU is not judged against the per-unit RRP." : ` A case SKU is judged by the rule "${gate.case_skus}".`;
  return `Amazon parity risk: a price below the region RRP on ${who} needs an RRP exception to commit.${currency}${cases}`;
}
/** Where the RRP is read from: the anchor marketplace, named as the anchor on its own row. */
export function anchorNote(lookup: { row: RrpRow; overlay: RrpOverlay }): string {
  const name = lookup.overlay.anchor?.marketplace_name ?? lookup.overlay.anchor?.marketplace_id ?? "";
  return lookup.row.is_anchor ? `${name}, anchor market` : name;
}
/** A row with no RRP: a case SKU names C2's case rule; any other reads the Config sentence for its region. */
export function notSetText(row: RrpRow, market: Market): string {
  return row.units_per_case != null && row.not_set ? row.not_set : RRP_COPY.noRrp(MARKET_LABEL[market]);
}
/** "PLN converted at the ECB rate of 24 Sept 2026" (and ", over 4 days old" when C2 says the rate is stale). */
export function convertedNote(row: RrpRow): string | null {
  if (row.fx_rate == null) return null;
  return RRP_COPY.converted(row.price_currency, humanDate(row.fx_rate_date), Boolean(row.fx_stale));
}
export function recordText(record: { reason: string; valid_until: string }): string {
  return RRP_COPY.exception(record.reason, humanDate(record.valid_until));
}

/** The drawer's RRP line: the value and the facts under it, all from the overlay row. */
export function rrpDetail(lookup: RrpLookup, market: Market): { value: string; meta: string } {
  if (lookup.kind === "pending") return { value: "—", meta: RRP_COPY.pending };
  if (lookup.kind === "failed") return { value: RRP_COPY.unreadShort, meta: RRP_COPY.unread };
  if (lookup.kind === "none") return { value: "—", meta: RRP_COPY.noRow };
  const r = lookup.row;
  if (r.rrp == null) return { value: notSetText(r, market), meta: r.units_per_case != null ? "" : RRP_COPY.setInConfig };
  const facts: string[] = [];
  if (r.deviation_pct != null) facts.push(deviationLabel(r.deviation_pct, feesMarked(r)));
  else if (r.comparable === false) facts.push(RRP_COPY.notComparable(r.price_currency));
  else facts.push(RRP_COPY.noPrice);
  const converted = convertedNote(r);
  if (converted) facts.push(converted);
  if (r.parity_risk) facts.push(RRP_COPY.parityRisk);
  if (r.exception) facts.push(recordText(r.exception) + (r.exception.ended ? ` (${RRP_COPY.ended})` : ""));
  else if (r.parity_risk) facts.push(RRP_COPY.noException);
  if (r.fee_reason) facts.push(feeReasonText(r.fee_reason));
  facts.push(r.gated ? RRP_COPY.gated : r.is_anchor ? RRP_COPY.anchorNotGated : RRP_COPY.notGated);
  if (r.is_anchor && r.anchor_drift && r.anchor_price) facts.push(RRP_COPY.drift(money(r.anchor_price, r.currency)));
  return { value: `${money(r.rrp, r.currency)} (${anchorNote(lookup)})`, meta: facts.join(" · ") };
}

export const RRP_COPY = {
  rrp: (price: string) => `RRP ${price}`,
  noRrp: (region: string) => `No RRP set for ${region}`,
  setInConfig: "set by the first price committed on the anchor market, or typed in Config, per product per region",
  followed: {
    lead: (n: number) => (n === 1 ? "The RRP now follows the committed anchor price for 1 product:" : `The RRP now follows the committed anchor price for ${n} products:`),
    item: (product: string, market: string, place: string, rrp: string, previous: string | null) =>
      `${product}, ${market}: ${rrp} from the ${place} price${previous === null ? " (first RRP)" : ` (was ${previous})`}`,
    note: "Config reads each one with the run and the line that set it; a typed RRP holds until the next anchor commit.",
  },
  pending: "reading the RRP",
  unread: "The RRP could not be read. Try again shortly.",
  unreadShort: "Not read",
  unreadCell: "RRP could not be read",
  noRow: "this row carries no RRP",
  noPrice: "no committed price to compare",
  notComparable: (currency: string) => `not comparable: no ECB rate from ${currency}`,
  converted: (currency: string, date: string, stale: boolean) => `${currency} converted at the ECB rate of ${date}${stale ? ", over 4 days old" : ""}`,
  parityRisk: "Amazon parity risk",
  exception: (reason: string, until: string) => `Exception: ${reason}, until ${until}`,
  ended: "ended",
  noException: "no exception recorded: a new price below the RRP needs one to commit",
  gated: "a price below it needs an RRP exception to commit",
  notGated: "this channel is not gated",
  anchorNotGated: "the anchor market, not gated",
  drift: (price: string) => `anchor price ${price} is off the RRP`,
  savedException: (reason: string, until: string) => `RRP exception saved: ${reason}, until ${until}`,
  feeReason: {
    mark: ", fees",
    link: "Record fee reason",
    label: "Why does this price stand above the RRP?",
    placeholder: "Reason, such as Shopify payment fees",
    save: "Save fee reason",
    cancel: "Cancel",
    empty: "Give a reason to record.",
    saved: (reason: string) => `Fee reason recorded: ${reason}`,
    line: (reason: string, facts: string) => `Fee reason: ${reason}${facts ? ` (${facts})` : ""}`,
    notAbove: (rrp: string) => `Not recorded: the committed price no longer stands above the RRP of ${rrp}. The Price Run reads again.`,
    noRrp: "Not recorded: this row carries no RRP now. The Price Run reads again.",
    notComparable: "Not recorded: the price cannot be compared with the RRP (no ECB rate is stored for its currency).",
    noPrice: "Not recorded: this row has no committed price. The Price Run reads again.",
    bad: "Not recorded: give a reason of at most 500 characters.",
  },
  box: {
    line: (price: string, note: string) => `RRP ${price} · ${note}, checked when you save`,
    reasonLabel: "Why is this price off RRP?",
    reasonPlaceholder: "Reason",
    untilLabel: "Until",
    note: "Amazon parity risk. The commit records the exception: who, why and until when.",
    saveException: "Save with RRP exception",
    both: "Give both a reason and an until date for the RRP exception.",
    recorded: (until: string) => ` RRP exception recorded until ${until}.`,
  },
  belowRrp: (price: string, rrp: string) =>
    `${price} is below the region RRP of ${rrp}: an Amazon parity risk. Give a reason and an until date to save.`,
  belowRrpConverted: (price: string, compared: string, date: string, stale: boolean, deviation: string | null, rrp: string) =>
    `${price} is ${compared} at the ECB rate of ${date}${stale ? " (over 4 days old)" : ""}, ${deviation != null ? `${deviation}% vs` : "below"} the region RRP of ${rrp}: an Amazon parity risk. Give a reason and an until date to save.`,
  badException: (day: string | null) =>
    `The RRP exception was not saved: the until date must be ${day ?? "today"} or later, and a reason over 500 characters is refused. Correct it and save again.`,
  rrpNotComparable: (price: string, currency: string, rrp: string) =>
    `${price} cannot be compared with the region RRP of ${rrp}: no ECB rate from ${currency} is stored. Give a reason and an until date to save.`,
  commitLine: {
    below_rrp: (saved: string, rrp: string) => `saved ${saved}, below the RRP of ${rrp}`,
    rrp_not_comparable: (saved: string, rrp: string) => `saved ${saved}, no ECB rate to compare with the RRP of ${rrp}`,
    rrp_exception_ended: (until: string) => `its exception ended ${until}`,
  },
  nothingCommittedRrp: (n: number, lines: string) =>
    `Nothing committed. ${n} ${n === 1 ? "line needs" : "lines need"} an RRP exception in force: ${lines}. Open each row, give a reason and an until date or change the price, then commit again.`,
  rrpChanged: "Nothing committed. An RRP changed in Config while this commit ran. The Price Run now reads the new RRP; commit again.",
  log: {
    title: "RRP exceptions",
    note: "Append-only record of every price committed below its region RRP, with the reason, the date it holds until, who recorded it and who committed it. Read only.",
    emptyTitle: "No RRP exceptions yet",
    emptyBody: "A price committed below its region RRP with an exception appears here.",
    noMatch: "No RRP exceptions match the filters",
    columns: ["Committed", "Product / Channel", "Region", "Price", "RRP", "Reason", "Until", "Who"],
    filters: { market: "Region", sku: "SKU", status: "Status" },
    statusLabel: { in_force: "In force", ended: "Ended" } as Record<RrpStatus, string>,
    trailing: (total: string) => `${total} ${total === "1" ? "exception" : "exceptions"} · newest first`,
    anchor: (name: string) => `from ${name}`,
    stagedBy: (email: string | null) => `Saved by ${personName(email)}`,
    approvedBy: (email: string) => `committed by ${personName(email)}`,
    footnote: "The commit writes one entry per price committed below its region RRP with an exception. Nothing happens when an exception ends: the next price saved below the RRP needs a new one. A price in another currency was compared at the latest rate stored when it was saved and committed (the ECB’s; for the dirham, the ECB’s US dollar rate times the UAE peg); the record keeps both currencies and no rate, so it shows no deviation. The period is the day of the commit, in UTC.",
    csv: {
      header: ["Committed (UTC)", "SKU", "Product", "Variant", "Region", "Channel", "Store", "Marketplace", "Price", "Currency", "RRP",
        "RRP currency", "Against RRP (%)", "Anchor market", "Reason", "Until", "Status", "Saved by", "Committed by", "Price Run"],
    },
  },
} as const;

/** The apply panel's line for the RRPs a commit wrote (C2 v1.43 `rrps`): one item per RRP, the product, its market, the
 * anchor marketplace named from the committed line the RRP followed (its id, else the marketplace id), the RRP and the
 * one it replaced; nothing is computed, every figure is C2's string through `money`. Empty when the commit wrote none. */
export function rrpsWrittenText(rrps: RrpWritten[], lines: Array<{ id: number; marketplace_id: string; marketplace_name: string | null }>): string | null {
  if (rrps.length === 0) return null;
  const items = rrps.map((r) => {
    const line = lines.find((l) => l.id === r.line_id);
    const place = line?.marketplace_name ?? line?.marketplace_id ?? r.anchor_market;
    return RRP_COPY.followed.item(`${r.tracking} ${r.variant}`, MARKET_LABEL[r.market] ?? r.market, place, money(r.rrp, r.currency),
      r.previous_rrp === null ? null : money(r.previous_rrp, r.currency));
  });
  return `${RRP_COPY.followed.lead(rrps.length)} ${items.join("; ")}. ${RRP_COPY.followed.note}`;
}
